import {
  useUpdate,
  useNotify,
  useRefresh,
  useTranslate,
  useRecordContext,
} from 'react-admin';
import { Button } from '@mui/material';
import { StarBorder } from '@mui/icons-material';
import { resolveErrorCode } from './errorMapping';

// "Set as default" row action (sub-plan 20 one-default-per-role UX). Shown only on NON-default rows.
// A single PUT { isDefault: true }; the backend swaps the prior default transactionally (sub-plan 17
// `ensureSingleDefault`), so this NORMALLY succeeds -> refresh the list. The UI never fabricates which
// other row is the default; it refetches server truth. Only a rare concurrent default-set race trips
// the partial-unique index -> AI_MODEL_CONFIG_CONFLICT (409, REF-08 §4): toast + refresh.
export const SetDefaultButton = () => {
  const record = useRecordContext();
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [update, { isLoading }] = useUpdate();

  if (!record || record.isDefault) return null;

  const handleClick = (e: React.MouseEvent) => {
    // Datagrid rowClick='edit' would otherwise navigate; keep the click on the action.
    e.stopPropagation();
    update(
      'ai-model-configs',
      { id: record.id, data: { isDefault: true }, previousData: record },
      {
        mutationMode: 'pessimistic',
        onSuccess: () => {
          notify('resources.ai-model-configs.notifications.defaultSet', { type: 'success' });
          refresh();
        },
        onError: (error: any) => {
          const code = resolveErrorCode(error);
          if (code === 'AI_MODEL_CONFIG_CONFLICT') {
            notify(
              translate('resources.ai-model-configs.errors.defaultConflict', {
                _: 'Could not set this model as default due to a conflict. Refresh and try again.',
              }),
              { type: 'error' },
            );
          } else {
            notify(
              error?.message ||
                translate('resources.ai-model-configs.errors.defaultFailed', {
                  _: 'Could not set this model as default.',
                }),
              { type: 'error' },
            );
          }
          // Refetch server truth regardless (no fabricated local state).
          refresh();
        },
      },
    );
  };

  return (
    <Button
      size="small"
      startIcon={<StarBorder />}
      disabled={isLoading}
      onClick={handleClick}
    >
      {translate('resources.ai-model-configs.actions.setDefault', { _: 'Set as default' })}
    </Button>
  );
};

export default SetDefaultButton;
